/* tslint:disable:max-line-length */
import { Inject, Injectable, NgZone } from '@angular/core';
import { AttachmentDbEntity, FaultDbEntity } from '@docma-mm-core/fault/fault-db.entity';
import { ChangeState } from '@docma-mm-core/model/change-state/change-state';
import { LoadState } from '@docma-mm-core/model/load-state/load-state';
import { SyncState } from '@docma-mm-core/model/sync-state/sync-state';
import { MmDbService } from '../../mm-db/mm-db-service';
import { MmProjectDbCollections } from '../../mm-db/mm-project-db-schema';
import { AttachmentData } from './attachment';
import { AttachmentFileDbEntity, FileDbEntity, mapFileEntityToFile } from './attachment-db.entity';
import { IOfflineService } from '../../mm-offline/interfaces/offline-service.interface';
import { OFFLINE_SERVICE } from '../../mm-offline/interfaces/offline-service.token';
import { LoadStatusEnum } from '@docma-mm-core/model/load-state/load-state.enum';
import { Logger } from '@mydocma/common/src/lib/logger';

@Injectable()
export class AttachmentDbService {
  constructor(private readonly mmDbService: MmDbService,
              @Inject(OFFLINE_SERVICE) private offlineService: IOfflineService,
              private ngZone: NgZone,
              private readonly logger: Logger) {

  }

  async getFile(mydocmaUid: string, uid: string): Promise<File> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.files as never, uid);
    if (entity) {
      if (entity.loadState.data.buffer === null){
        const blob = await this.offlineService.getAttachment(entity.uid, entity.loadState.data.name);
        const file = new File([blob], entity.loadState.data.name, {type: entity.loadState.data.mimeType});
        return file;
      }

      return await this.mapAttachmentFileEntityToFile(entity);
    }
    return null;
  }

  private mapAttachmentFileEntityToFile(entity: AttachmentFileDbEntity): File {
    if (!entity.loadState || !entity.loadState.data || !entity.loadState.data.buffer) {
      return null;
    }
    const file = mapFileEntityToFile(entity.loadState.data);
    return file;
  }



  async saveChangeState(mydocmaUid: string, faultUid: string, activityUId: string, groupUid: string, attachmentUid: string, changeState: ChangeState<AttachmentData>): Promise<ChangeState<AttachmentData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (entity) {
        try {
          const attachmentEntity = getAttchmantFromEntity(entity, activityUId, groupUid, attachmentUid);
          if (attachmentEntity) {
            attachmentEntity.changeState = changeState;
          }
        } catch (err) {
          throw new Error(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message);
        }
        await db.set(MmProjectDbCollections.faults as never, entity);
      }
      return new ChangeState<AttachmentData>(changeState);
    });
  }

  async saveLoadStates(mydocmaUid: string,
                       groupUid: string,
                       items: {faultUid: string, activityUid: string, attachmentUid: string}[],
                       loadState: LoadState<AttachmentData>,
                       throwErrorIfNotFound?: boolean): Promise<LoadState<AttachmentData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(items.map(x => x.faultUid)));
      if (!entities) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting attachment change state. Faults not found in project store ${mydocmaUid}`);
        } else {
          this.logger.error(`An error while persisting attachment change state. Faults not found in project store ${mydocmaUid}`, '', 'AttachmentDbService');
        }
        return null;
      }

      try {
        const loadedEntities: any[] = [];
        for (const item of items) {
          const entity = entities.find(x => x.uid === item.faultUid);
          const attachmentEntity = getAttchmantFromEntity(entity, item.activityUid, groupUid, item.attachmentUid);
          if (!attachmentEntity) {
            throw new Error(`Attachment uid ${item.faultUid}/${item.attachmentUid} not found in project store ${mydocmaUid}`);
          }
          attachmentEntity.loadState = loadState;
          attachmentEntity.id = loadState.data?.Id ?? attachmentEntity.id;
          loadedEntities.push(entity);
        }
        await db.setAll(MmProjectDbCollections.faults as never, loadedEntities);
      } catch (err) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message);
        } else {
          this.logger.warn(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message, 'AttachmentDbService');
        }
        return null;
      }
      return new LoadState<AttachmentData>(loadState);
    });
  }

  async saveLoadState(mydocmaUid: string, faultUid: string, activityUId: string, groupUid: string, attachmentUid: string, loadState: LoadState<AttachmentData>, throwErrorIfNotFound?: boolean): Promise<LoadState<AttachmentData>> {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (!entity) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting attachment change state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`);
        } else {
          this.logger.error(`An error while persisting attachment change state. Fault uid ${faultUid} not found in project store ${mydocmaUid}`, '', 'AttachmentDbService');
        }
        return null;
      }

      try {
        const attachmentEntity = getAttchmantFromEntity(entity, activityUId, groupUid, attachmentUid);
        if (!attachmentEntity) {
          throw new Error(`Attachment uid ${faultUid}/${attachmentUid} not found in project store ${mydocmaUid}`);
        }
        attachmentEntity.loadState = loadState;
        attachmentEntity.id = loadState.data?.Id ?? attachmentEntity.id;
      } catch (err) {
        if (throwErrorIfNotFound) {
          throw new Error(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message);
        } else {
          this.logger.warn(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message, 'AttachmentDbService');
        }
        return null;
      }
      await db.set(MmProjectDbCollections.faults as never, entity);
      return new LoadState<AttachmentData>(loadState);
    });
  }

  async saveLoadStateStatus(mydocmaUid: string, faultUid: string, attachmentUid: string, activityUid: string, loadStateStatus: LoadStatusEnum, throwErrorIfNotFound?: boolean): Promise<LoadState<AttachmentData>> {
    const db = await this.mmDbService.getProjectDb(mydocmaUid);
    const entity = await db.get(MmProjectDbCollections.files as never, attachmentUid);
    const faultEntity = await db.get(MmProjectDbCollections.faults as never, faultUid);
    if (!entity || !faultEntity)  {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting attachment change state. Attachment uid ${attachmentUid} not found in project store ${mydocmaUid}`);
      } else {
        console.error(`An error while persisting attachment change state. Attachment uid ${attachmentUid} not found in project store ${mydocmaUid}`);
      }
      return null;
    }

    try
    {
      entity.loadState.status = loadStateStatus;
      await db.set(MmProjectDbCollections.files as never, entity);

      const attachmentEntity = getAttchmantFromEntity(faultEntity, activityUid, null, attachmentUid) ;
      if (!attachmentEntity) {
        throw new Error(`Attachment uid ${faultUid}/${attachmentUid} not found in project store ${mydocmaUid}`);
      }
      attachmentEntity.loadState.status = loadStateStatus;
      await db.set(MmProjectDbCollections.faults as never, faultEntity);
    } catch (err) {
      if (throwErrorIfNotFound) {
        throw new Error(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message);
      } else {
        console.log(`An error while persisting attachment change state in project ${mydocmaUid}.` + err.message);
      }
    }

    return new LoadState<AttachmentData>(entity.loadState);
  }

  async saveSyncStates(mydocmaUid: string,
                       groupUid: string,
                       items: {faultUid: string, activityUid: string, attachmentUid: string}[],
                       state: SyncState,
                       throwErrorIfNotFound?: boolean) {

    // const faultMapInternal = Object.entries(faultMap).map(([aUid, fUid]) => {faultUid: fUid, activityUid: aUid});
    // const faultUids: any[] = Object.values(faultMap);
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entities = await db.getAll(MmProjectDbCollections.faults as never, IDBKeyRange.bind(items.map(x => x.faultUid)));
      if (entities) {
        try {
          const updateEntities: any[] = [];
          for (const item of items) {
            const entity = entities.find(x => x.uid === item.faultUid);
            const attachmentEntity = getAttchmantFromEntity(entity, item.activityUid, groupUid, item.attachmentUid);
            if (!attachmentEntity) {
              throw new Error(`Attachment uid ${item.faultUid}/${item.attachmentUid} not found in project store ${mydocmaUid}`);
            }
            attachmentEntity.syncState = {...state};
            delete attachmentEntity.syncState.changes;
            updateEntities.push(entity);
          }

          await db.putAll(MmProjectDbCollections.faults as never, updateEntities);
        } catch (err) {
          // TODO: Log error
          if (throwErrorIfNotFound) {
            throw new Error(`An error while persisting attachment sync state in project ${mydocmaUid}.` + err.message);
          } else {
            this.logger.warn(`An error while persisting attachment sync state in project ${mydocmaUid}.` + err.message, 'AttachmentDbService');
          }
        }
      }
      return state;
    });
  }

  async saveSyncState(mydocmaUid: string, faultUid: string, activityId: any, groupUid: string, attachmentId: any, state: SyncState, throwErrorIfNotFound?: boolean) {
    return await this.ngZone.runOutsideAngular(async () => {
      const db = await this.mmDbService.getProjectDb(mydocmaUid);
      const entity = await db.get(MmProjectDbCollections.faults as never, faultUid);
      if (entity) {
        try {
          const attachmentEntity = getAttchmantFromEntity(entity, activityId, groupUid, attachmentId);
          if (!attachmentEntity) {
            throw new Error(`Attachment uid ${faultUid}/${attachmentId} not found in project store ${mydocmaUid}`);
          }
          attachmentEntity.syncState = {...state};
          delete attachmentEntity.syncState.changes;
          await db.set(MmProjectDbCollections.faults as never, entity);

        } catch (err) {
          // TODO: Log error
          if (throwErrorIfNotFound) {
            throw new Error(`An error while persisting attachment sync state in project ${mydocmaUid}.` + err.message);
          } else {
            this.logger.warn(`An error while persisting attachment sync state in project ${mydocmaUid}.` + err.message, 'AttachmentDbService');
          }
        }
      }
      return state;
    });
  }
}

export function getAttchmantFromEntity(faultEntity: FaultDbEntity, activityId: any, groupUid: string, attachmentId: any) {
  let attachmentEntity: AttachmentDbEntity;
  if (activityId || groupUid) {
    const  activityEntity = faultEntity.activities.find(a =>
      (activityId && !isNaN(+activityId) && a.id === activityId) || (activityId && !!isNaN(+activityId) && a.uid === activityId) || (groupUid && a.guid === groupUid));

    if (!activityEntity) {
      throw new Error(`Activity uid ${faultEntity.uid}/${activityId} not found in project store`);
    }
    attachmentEntity = activityEntity.attachments.find(a => (attachmentId && !isNaN(+attachmentId) && a.id === attachmentId) || (attachmentId && !!isNaN(+attachmentId) && a.uid === attachmentId) );
    return attachmentEntity;
  } else {
    attachmentEntity = faultEntity.attachments.find(a => (attachmentId && !isNaN(+attachmentId) && a.id === attachmentId) || (attachmentId && !!isNaN(+attachmentId) && a.uid === attachmentId) );
    return attachmentEntity;
  }
}
